import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireScopes, rateLimitFunction } from "~/lib/middleware"
import { ApiResponse, Organization, organizationSchema } from "~/lib/schemas"
import { LogtoScopes } from "~/lib/logto/logto"
import { ScopeRule, testScopes } from "~/lib/scopes"
import { ErrorResponse } from "~/lib/errors"

// Utility function to fetch organization data without middleware requirements
export async function fetchOrganizationData(organizationId: string): Promise<Organization | null> {
  // Use real API
  const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
  const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
  
  if (!BFF_API_KEY) {
    throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
  }
  
  const params = new URLSearchParams({ organization_id: organizationId })
  
  const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/info?${params}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${BFF_API_KEY}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    if (response.status === 404) return null;

    const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
    console.error("ERROR FETCHING ORG: ", errorData, organizationId)
    
    throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
  }
  
  const result = await response.json() as ApiResponse<Organization>
  
  return organizationSchema.parse(result.data)
}

export const getOrg = createServerFn({
  method: 'GET',
})
  .middleware([requireAuth, rateLimitFunction])
  .validator((id: string) => id)
  .handler(async (ctx) => {
    const organizationId = ctx.data
    // console.log("ORG CTX", ctx.context)
    const hasAdminOrgReadScope = testScopes(ctx.context.accessToken?.scopes ?? [], LogtoScopes.AdminOrgRead)
    const userIsInOrg = ctx.context.accessToken.organization_id === organizationId

    if (!(hasAdminOrgReadScope || userIsInOrg)) {
      throw new ErrorResponse("No permission to view this resource", 403, 'ORG_ACCESS_DENIED')
    }

    //test to see if the access token has the current org id in its claims
    // const hasOrgIdInClaims = ctx.context.accessToken..?.includes(organizationId)
    
    return await fetchOrganizationData(organizationId)
  })


